import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { Field } from "./_shared";

export default function FamilyTab({ client, onSave, canEdit }) {
  const [spouse, setSpouse] = useState(client.spouse || {});
  const [children, setChildren] = useState(client.children || []);
  const [parents, setParents] = useState(client.dependent_parents || []);

  const save = () => onSave({ spouse, children, dependent_parents: parents });

  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-3 font-dashboard text-base font-bold">Spouse</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Name" v={spouse.name} on={(v) => setSpouse({ ...spouse, name: v })} disabled={!canEdit} testId="spouse-name" />
          <Field label="Date of birth" type="date" v={spouse.dob?.slice(0,10)} on={(v) => setSpouse({ ...spouse, dob: v })} disabled={!canEdit} testId="spouse-dob" />
          <Field label="Occupation" v={spouse.occupation} on={(v) => setSpouse({ ...spouse, occupation: v })} disabled={!canEdit} testId="spouse-occupation" />
        </div>
      </section>

      <ChildrenSection items={children} setChildren={setChildren} canEdit={canEdit} />
      <ParentsSection parents={parents} setParents={setParents} canEdit={canEdit} />

      {canEdit && <Button onClick={save} data-testid="save-family-btn">Save family</Button>}
    </div>
  );
}

function ChildrenSection({ items, setChildren, canEdit }) {
  const children = items;
  const add = () => setChildren([...children, { id: crypto.randomUUID(), name: "", dob: "", schooling_start_age: 5, higher_education_age: 18 }]);
  const rm = (id) => setChildren(children.filter((c) => c.id !== id));
  const upd = (id, k, v) => setChildren(children.map((c) => (c.id === id ? { ...c, [k]: v } : c)));
  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-dashboard text-base font-bold">Children</h3>
        {canEdit && <Button size="sm" variant="outline" onClick={add} data-testid="add-child-btn"><Plus className="mr-1 h-3 w-3" /> Add child</Button>}
      </div>
      {children.length === 0 && <p className="text-sm text-muted-foreground">No children on record.</p>}
      <div className="space-y-3">
        {children.map((c) => (
          <div key={c.id} className="grid gap-3 rounded-md border border-border p-4 md:grid-cols-5">
            <Field label="Name" v={c.name} on={(v) => upd(c.id, "name", v)} disabled={!canEdit} testId={`child-name-${c.id}`} />
            <Field label="DOB" type="date" v={c.dob?.slice(0,10)} on={(v) => upd(c.id, "dob", v)} disabled={!canEdit} testId={`child-dob-${c.id}`} />
            <Field label="Schooling start" type="number" v={c.schooling_start_age} on={(v) => upd(c.id, "schooling_start_age", Number(v))} disabled={!canEdit} testId={`child-school-${c.id}`} />
            <Field label="Higher ed age" type="number" v={c.higher_education_age} on={(v) => upd(c.id, "higher_education_age", Number(v))} disabled={!canEdit} testId={`child-highered-${c.id}`} />
            {canEdit && (
              <div className="flex items-end">
                <Button size="sm" variant="ghost" onClick={() => rm(c.id)} data-testid={`rm-child-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function ParentsSection({ parents, setParents, canEdit }) {
  const add = () => setParents([...parents, { id: crypto.randomUUID(), name: "", dob: "", dependency_level: "Partial", monthly_support_amount: 0 }]);
  const rm = (id) => setParents(parents.filter((c) => c.id !== id));
  const upd = (id, k, v) => setParents(parents.map((c) => (c.id === id ? { ...c, [k]: v } : c)));
  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-dashboard text-base font-bold">Dependent parents</h3>
        {canEdit && <Button size="sm" variant="outline" onClick={add} data-testid="add-parent-btn"><Plus className="mr-1 h-3 w-3" /> Add parent</Button>}
      </div>
      {parents.length === 0 && <p className="text-sm text-muted-foreground">None on record.</p>}
      <div className="space-y-3">
        {parents.map((p) => (
          <div key={p.id} className="grid gap-3 rounded-md border border-border p-4 md:grid-cols-5">
            <Field label="Name" v={p.name} on={(v) => upd(p.id, "name", v)} disabled={!canEdit} testId={`parent-name-${p.id}`} />
            <Field label="DOB" type="date" v={p.dob?.slice(0,10)} on={(v) => upd(p.id, "dob", v)} disabled={!canEdit} />
            <Field label="Dependency" v={p.dependency_level} on={(v) => upd(p.id, "dependency_level", v)} disabled={!canEdit} />
            <Field label="Support ₹/mo" type="number" v={p.monthly_support_amount} on={(v) => upd(p.id, "monthly_support_amount", Number(v))} disabled={!canEdit} />
            {canEdit && (
              <div className="flex items-end">
                <Button size="sm" variant="ghost" onClick={() => rm(p.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
